import React from 'react';
import { motion } from 'framer-motion';
import { useConfig } from '@context/ConfigContext';
import AnimatedCounter from '@components/shared/AnimatedCounter/AnimatedCounter';
import Carousel from '@components/shared/Carousel/Carousel';
import styles from './About.module.css';

const About: React.FC = () => {
  const { config } = useConfig();

  if (!config || !config.components.about.enabled) return null;

  const { about } = config.components;

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: 'easeOut',
      },
    },
  };

  return (
    <section id="about" className={styles.about}>
      <div className={styles.container}>
        <motion.div
          className={styles.content}
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
        >
          {/* Section Header */}
          <motion.div className={styles.header} variants={itemVariants}>
            <h2 className={styles.title}>About Us</h2>
            <div className={styles.divider}></div>
          </motion.div>

          {/* Main Content - Two Column Layout */}
          <div className={styles.mainContent}>
            <motion.div className={styles.textContent} variants={itemVariants}>
              <div
                className={styles.text}
                dangerouslySetInnerHTML={{ __html: about.text }}
              />
              {about.mission && (
                <div className={styles.mission}>
                  <h3 className={styles.missionTitle}>Our Mission</h3>
                  <p className={styles.missionText}>{about.mission}</p>
                </div>
              )}
            </motion.div>

            <motion.div className={styles.imageContent} variants={itemVariants}>
              {about.teamGallery.enabled && about.teamGallery.images.length > 0 ? (
                <Carousel
                  autoplay
                  autoplaySpeed={3000}
                  dots
                  arrows={false}
                  fade
                  className={styles.carousel}
                >
                  {about.teamGallery.images.map((image, index) => (
                    <div key={index} className={styles.carouselSlide}>
                      <img
                        src={image}
                        alt={`Team ${index + 1}`}
                        className={styles.teamImage}
                      />
                    </div>
                  ))}
                </Carousel>
              ) : (
                <img
                  src={about.teamPhoto}
                  alt="Our Team"
                  className={styles.teamImage}
                />
              )}
            </motion.div>
          </div>

          {/* Stats Section */}
          {about.stats && about.stats.length > 0 && (
            <motion.div className={styles.stats} variants={itemVariants}>
              {about.stats.map((stat, index) => (
                <div key={index} className={styles.statItem}>
                  <i className={`${stat.icon} ${styles.statIcon}`} aria-hidden="true"></i>
                  <div className={styles.statNumber}>
                    <AnimatedCounter
                      value={stat.number}
                      duration={2}
                      suffix={stat.suffix || ""}
                    />
                  </div>
                  <p className={styles.statLabel}>{stat.label}</p>
                </div>
              ))}
            </motion.div>
          )}

          {/* Certifications */}
          {about.certifications && about.certifications.length > 0 && (
            <motion.div className={styles.certifications} variants={itemVariants}>
              <div className={styles.certificationsList}>
                {about.certifications.map((cert, index) => (
                  <div key={index} className={styles.certificationItem}>
                    <i className={`${cert.icon} ${styles.certificationIcon}`} aria-hidden="true"></i>
                    <span className={styles.certificationName}>{cert.name}</span>
                  </div>
                ))}
              </div>
            </motion.div>
          )}
        </motion.div>
      </div>
    </section>
  );
};

export default About;